import angular from 'angular';
import uiBootstrap from 'angular-ui-bootstrap';

import csmUtilTools from '../../util/tools/tools';

export default angular
  .module('csm.ui.numberInput', [uiBootstrap, csmUtilTools])
  /**
   * @ngdoc    directive
   * @module   csm.books.dom
   * @name     csm.books.dom.directive:numberInput
   * @restrict A
   * @property {expr} allowNegative
   * Allow negative number input flag. Default is to ignore negative numbers.
   *
   * @property {expr} blankIsNan     `true`=Blank input is NaN, `false`=0.
   * Convert blank input to NaN flag. When `true`, empty input is converted to NaN
   * instead of zero.
   *
   * @property {expr} ignoreInvalid
   * Ignore invalid number input flag. When `true`, illegal characters will be
   * ignored and validation will return `true`. For example, for a model value of
   * 100, entering 'xxx' will restore the input field to `100` on blur, while
   * entering '10x' will set it to '10'. By default, validation will be false and
   * input field will not be updated on blur.
   *
   * This option should not be used if this directive is used in `angular-ui-grid`.
   * If the validator returns `false`, the grid will fully restore the model value
   * so this flag is not necessary.
   *
   * @property {expr} noFormatOnBlur
   * Turns off formatting of the model value on blur event. Normally, the model
   * value is formatted and the input field is updated. But this will not work
   * if `ngModelOptions` updates on `blur` or after `debounce` period. Use this
   * flag if these options are required.
   *
   * @property {expr} noInputValidation
   * Turns off validator. When validation is on, validation failure results in
   * model value set to `undefined` or, if `ng-model-options.allowInvalid` is
   * `true`, no change to the model value. Set this flag if the model value should
   * be set to the invalid value--such as when another validator is used.
   *
   * Because of the way `ngModel` is written, potentially using asychronous
   * validators, it would be very difficult to intercept the change in the model
   * value for output formatting. However, it might be done as follows:
   *
   * 1) Overload ngModel.$setViewValue and set a flag indicating a change is coming
   *    before call the parent method.
   *
   * 2) Overload ngModel.$commitViewValue, call the parent, which will run the
   *    synchronous validators, and watch ngModel.$pending for all the async
   *    validators to run (unsure if $pending is actually used but the docs say it
   *    is), and somehow determine if the model value is changed.
   *
   * @property {expr} precision
   * Rounding precision for formatting.
   *
   * @property {expr} zeroAsBlank
   * When `true`, zero values display as the empty string.
   *
   * @description
   * Attribute for text input to be formatted and parsed as number. Invalid input
   * is ignored and leaves the model value unchanged. Model value is formatted and
   * displayed on blur unless `noFormatOnBlur` is `true`.
   *******************************************************************************/
  .directive('numberInput', function ($injector) {
    return {
      restrict: 'A',
      scope: {
        allowNegative: '&',
        blankIsNan: '&',
        ignoreInvalid: '&',
        noFormatOnBlur: '&',
        noInputValidation: '&',
        precision: '&',
        zeroAsBlank: '&',
        selectOnFocus: '&'
      },
      require: 'ngModel',
      link: function (scope, elem, attrs, ngModel) {
        var $svcs = {
          attrs: attrs,
          elem: elem,
          ngModel: ngModel,
          scope: scope
        };
        scope.formatter = $injector.invoke(formatter, null, $svcs);

        ngModel.$formatters.unshift(scope.formatter);
        ngModel.$parsers.unshift($injector.invoke(parser, null, $svcs));
        ngModel.$validators.validator = $injector.invoke(
          validator,
          null,
          $svcs
        );
        elem.bind('blur', $injector.invoke(onBlur, null, $svcs));
        elem.bind('focus', $injector.invoke(onFocus, null, $svcs));
      }
    };
  }).name;

/*******************************************************************************
 * formatter */
/**
 * Returns the model value formatted to `scope.precision`. Returns empty string
 * for zero if `scope.zeroAsBlank` is `true`.
 *
 * @param  {Number} modelValue  Model value.
 * @return {string} Formatted `modelValue`.
 *******************************************************************************/
/* ngInject */
function formatter(scope, Tools) {
  return function (modelValue) {
    var n = Number(modelValue);

    /*-----------------------------------------*/
    /* Option: Return empty string for zero.   */
    /*                                         */
    /* Note: modelValue may be a string        */
    /*       here because of other formatters. */
    /*-----------------------------------------*/
    if (n === 0 && scope.zeroAsBlank()) return '';

    /*------------------------------------------*/
    /* Return model value rounded to precision. */
    /*------------------------------------------*/
    return isNaN(n)
      ? ''
      : Tools.formatToPrecision(modelValue, scope.precision() || 0);
  };
}

/*******************************************************************************
 * onBlur */
/**
 * Updates the element value with the formatted model value when the caret leaves
 * the field.
 *
 * Note: This will not work with `ngModelOptions.updateOn:'blur'` because it
 *       fires before the update. Also, it does not work in angular-ui-grid
 *       since the editor element is destroyed before the `blur` is seen (This
 *       may be from CSMCommonWeb/ui-grid.js changes made to `angular-ui-grid`).
 *
 * TODO: In the latest version (1.7) of AngularJS, this can be done with a call
 * TODO: to `ngModel.$processModelValue()`.
 *******************************************************************************/
/* ngInject */
function onBlur(elem, ngModel, scope) {
  return function () {
    if (!scope.noFormatOnBlur()) elem.val(scope.formatter(ngModel.$modelValue));
  };
}

/*****************************************************************************
 * onFocus */
/**
 * Selects all text within the input if when gaining focus if the
 * select-on-focus attribute is true.
 *****************************************************************************/
/* @ngInject */ function onFocus(scope) {
  return function () {
    if (scope.selectOnFocus()) this.select();
  };
}

/*******************************************************************************
 * parser */
/**
 * Parses the model value from the view value according to options.
 *
 * @param  {string} viewValue  View value.
 * @return {Number} Parsed `viewValue`.
 *******************************************************************************/
/* ngInject */
function parser(ngModel, scope) {
  return function (viewValue) {
    /*-----------------------------------------------------------------*/
    /* User cleared the input. Return NaN if requested, else return 0. */
    /*-----------------------------------------------------------------*/
    if (ngModel.$isEmpty(viewValue.trim())) return scope.blankIsNan() ? NaN : 0;

    /*-----------------------------------------------*/
    /* Remove whitespace, commas, and leading dollar */
    /* signs from view value and convert to number.  */
    /*-----------------------------------------------*/
    var vv = viewValue.replace(/[\s,]/g, '').replace(/^(\-?)\$/, '$1');
    var rv = Number(vv);

    /*--------------------------------------------------------------------*/
    /* Parsed value is negative. If not allowed, restore the              */
    /* current model value.                                               */
    /*                                                                    */
    /* TODO: This should be done in the validator by resetting the model  */
    /* TODO: value and returning `true`. See: 'noInputValidation' option. */
    /*--------------------------------------------------------------------*/
    if ((vv === '-' || rv < 0) && !scope.allowNegative())
      return ngModel.$modelValue;

    /*--------------------------------------------------*/
    /* Input could not be parsed. If ignoring, restore  */
    /* the current model value, else return bad string. */
    /*--------------------------------------------------*/
    if (ngModel.$isEmpty(vv) || isNaN(rv))
      return scope.ignoreInvalid() ? ngModel.$modelValue : vv;

    return rv;
  };
}

/*******************************************************************************
 * validator */
/**
 * Returns `false` if `modelValue` is NaN. This will signal `angular-ui-grid` to
 * restore the model value.
 *
 * TODO: Also returns `true` if `modelValue` is NaN and `viewValue` is empty.
 * TODO: This is a hack and can only happen if `scope.blankAsNaN` is `true`. Only
 * TODO: WriteChecks uses this feature and it's unclear why. Should be removed.
 *
 * @param  {Number}  modelValue  Model value.
 * @param  {string}  viewValue  View value.
 * @return {boolean} `true`=input is valid.
 *******************************************************************************/
/* @ngInject */
function validator(scope, ngModel) {
  return function (modelValue, viewValue) {
    if (scope.noInputValidation()) return true;
    else if (ngModel.$isEmpty(viewValue) === '' && scope.blankIsNan())
      return true;
    else return !isNaN(Number(modelValue));
  };
}
